// This is taken from palmetto components SelectInput.tsx and adapted to fit the needs of the AsyncSelect

import {
  Box,
  FormLabel,
  generateResponsiveClasses,
  Icon,
  ResponsiveProp,
  SimulatedEventPayloadType,
  Z_INDEX_VALUES,
} from '@palmetto/palmetto-components';
import classNames from 'classnames';
import { FC, FocusEvent, FocusEventHandler, ReactNode, useCallback, useRef, useState } from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import styles from './AsyncSelect.module.scss';
import { InputValidationMessage } from './InputValidationMessage';

export type AsyncSelectInputOption = {
  label: string;
  value: string;
};

export type AsyncSelectInputGroup = {
  label: string;
  options: AsyncSelectInputOption[];
};

export type AsyncSelectInputOptions = AsyncSelectInputOption[] | AsyncSelectInputGroup[];

export interface AsyncSelectInputProps {
  /**
   * The id attribute of the input.
   */
  id: string;
  /**
   * Custom content to be displayed above the input. If the label is hidden, will be used to set aria-label attribute.
   */
  label: string;
  /**
   * Callback function to call on change event.
   */
  onChange: (event: SimulatedEventPayloadType) => void;
  /**
   * Callback function to async load options.
   */
  loadOptions: (inputValue: string) => Promise<AsyncSelectInputOptions> | void;
  /**
   * Options for dropdown list.
   */
  defaultOptions?: AsyncSelectInputOptions;
  /**
   * When true, call `loadOptions('')` each time the menu opens (not on mount) so the dropdown
   * shows browse results before the user types. Gate this on any data the search depends on
   * (e.g. a selected equipment type) so the browse request only fires once that data exists.
   */
  loadOnOpen?: boolean;
  /**
   * The value(s) of select.
   */
  value: any | any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  /**
   * Autofocus select input on render.
   */
  autoFocus?: boolean;
  /**
   * Additional classes to add.
   */
  className?: string;
  /**
   * Mark the input field as invalid and display a validation message.
   * Pass a string or node to render a validation message below the input.
   */
  error?: ReactNode;
  /**
   * Additional clarifying text to help describe the input
   */
  helpText?: ReactNode;
  /**
   * Visually hide the label.
   */
  hideLabel?: boolean;
  /**
   * If the input value is clearable programmatically.
   */
  isClearable?: boolean;
  /**
   * If the input should be disabled and not focusable.
   */
  isDisabled?: boolean;
  /**
   * Is multi select enabled.
   */
  isMulti?: boolean;
  /**
   * The required and aria-required attributes on the input
   */
  isRequired?: boolean;
  /**
   * Select 'name' attribute.
   */
  name?: string;
  /**
   * A ref to portal the dropdown menu to. This is useful when the dropdown is located in a smaller container
   * and we want to avoid cutting off the menu.
   */
  menuPortalTarget?: HTMLElement;
  /**
   * Callback function to call on blur event.
   */
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  /**
   * Callback function to call on focus event.
   */
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  /**
   * Placeholder for input.
   */
  placeholder?: string;
  /**
   * Visual indicator that the field is required, that gets appended to the label
   */
  requiredIndicator?: ReactNode;
  /**
   * The size of the text input.
   */
  size?: 'sm' | 'md' | 'lg' | ResponsiveProp<'sm' | 'md' | 'lg'>;
  cacheOptions?: boolean;
  noOptionsMessage?: (obj: { inputValue: string | React.JSX.Element }) => string | React.JSX.Element;
  /**
   * Additional props to be spread. These will be applied specifically to
   * the `react-select` component that powers the select. For full docs on
   * react-select props, [Click Here](https://react-select.com/props)
   */
  [x: string]: any; // eslint-disable-line
}

export const AsyncSelectInput: FC<AsyncSelectInputProps> = ({
  id,
  label,
  onChange,
  defaultOptions,
  loadOnOpen = false,
  loadOptions,
  value,
  autoFocus = false,
  className = '',
  error = false,
  helpText,
  hideLabel = false,
  isClearable = false,
  isDisabled = false,
  isMulti = false,
  isRequired = false,
  menuPortalTarget = null,
  name = '',
  onFocus = null,
  onBlur = null,
  placeholder,
  requiredIndicator = ' *',
  size = 'md',
  cacheOptions = false,
  noOptionsMessage,
  ...restProps
}) => {
  // `loadOnOpen` fires the browse search when the menu opens (not on mount). The result is fed
  // back through `defaultOptions` as an array, which react-select treats as reactive.
  const [browseOptions, setBrowseOptions] = useState<AsyncSelectInputOptions | undefined>(undefined);
  const [isBrowseLoading, setIsBrowseLoading] = useState(false);
  // Tracks the in-flight browse fetch so we can discard its result (and clear the loading flag)
  // if the menu closes — or if the user types — before it settles. The debounced loadOptions
  // drops its earlier promise without settling it when a newer call clears the timer, so without
  // this guard the awaited browse promise can hang and leave isBrowseLoading stuck on `true`.
  const browseRequestRef = useRef<{ cancelled: boolean } | null>(null);

  const handleMenuOpen = useCallback(async () => {
    if (!loadOnOpen) return;
    const browseRequest = { cancelled: false };
    browseRequestRef.current = browseRequest;
    setIsBrowseLoading(true);
    try {
      const loaded = await loadOptions('');
      if (!browseRequest.cancelled) setBrowseOptions((loaded as AsyncSelectInputOptions) ?? []);
    } finally {
      if (!browseRequest.cancelled) setIsBrowseLoading(false);
    }
  }, [loadOnOpen, loadOptions]);

  const handleMenuClose = useCallback(() => {
    if (!loadOnOpen) return;
    if (browseRequestRef.current) browseRequestRef.current.cancelled = true;
    setBrowseOptions(undefined);
    setIsBrowseLoading(false);
  }, [loadOnOpen]);

  // Once the user types a real query the browse fetch is moot. The debounce abandons the browse
  // promise (clears its timer without settling it), so cancel the request and drop the browse
  // loading flag here — react-select drives its own loading state for the typed search.
  const handleLoadOptions = useCallback(
    (input: string) => {
      if (loadOnOpen && input) {
        if (browseRequestRef.current) browseRequestRef.current.cancelled = true;
        setIsBrowseLoading(false);
      }
      return loadOptions(input);
    },
    [loadOnOpen, loadOptions],
  );

  const handleChange = (values: any) => {
    const simulatedEventPayloadType: SimulatedEventPayloadType = {
      target: {
        name,
        value: values,
      },
    };

    onChange(simulatedEventPayloadType);
  };

  const handleFocus: FocusEventHandler = (e: any) => {
    if (onFocus) onFocus(e);
  };

  const handleBlur: FocusEventHandler = (e: any) => {
    if (onBlur) onBlur(e);
  };

  const responsiveClasses = generateResponsiveClasses('size', size);

  const wrapperClasses = classNames(
    'palmetto-components__variables__form-control',
    'select-input-wrapper',
    className,
    ...responsiveClasses.map((c) => styles[c]),
    {
      [styles.disabled]: isDisabled,
    },
  );

  const inputClasses = classNames('react-select', { [styles.error]: error });

  const labelProps = {
    inputId: id,
    helpText,
    className: styles['select-input-label'],
    isDisabled,
    isFieldRequired: isRequired,
    requiredIndicator,
  };

  const ClearIndicator = (props: any) => (
    <components.ClearIndicator {...props}>
      <Icon name="remove-light" />
    </components.ClearIndicator>
  );

  const DropdownIndicator = (props: any) => (
    <components.ClearIndicator {...props}>
      <Icon name="caret-down" />
    </components.ClearIndicator>
  );

  return (
    <Box width="100%" className={wrapperClasses}>
      {label && !hideLabel && <FormLabel {...labelProps}>{label}</FormLabel>}
      <AsyncSelect
        {...restProps}
        inputId={id}
        aria-label={label}
        components={{ ClearIndicator, DropdownIndicator }}
        aria-labelledby={label && !hideLabel ? `${id}Label` : undefined}
        className={inputClasses}
        classNamePrefix="react-select"
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti={isMulti}
        menuPortalTarget={menuPortalTarget}
        name={name}
        autoFocus={autoFocus}
        defaultOptions={loadOnOpen ? browseOptions : defaultOptions}
        isLoading={isBrowseLoading}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        cacheOptions={cacheOptions}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        loadOptions={handleLoadOptions}
        noOptionsMessage={noOptionsMessage}
        styles={{
          menuPortal: (base: any) => ({
            ...base,
            zIndex: Number(Z_INDEX_VALUES.popover),
          }),
        }}
        value={value}
      />
      {error && typeof error !== 'boolean' && <InputValidationMessage>{error}</InputValidationMessage>}
    </Box>
  );
};
